import {
  addDoc,
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { toDRXError } from '@/lib/drx-error';

export type AdminAuditAction =
  | 'PASSWORD_RESET'
  | 'USER_CREATED'
  | 'USER_UPDATED'
  | 'USER_DELETED'
  | 'USERNAME_CHECK'
  | 'EMAIL_SENT';

export interface AdminAuditLogEntry {
  id?: string;
  adminUserId: string;
  targetUserId?: string;
  action: AdminAuditAction;
  details: Record<string, unknown>;
  timestamp: Date;
  ipAddress?: string;
  userAgent?: string;
}

export class AdminAuditRepository {
  /** Client may create here per `firestore.rules`; `adminActionLogs` is server-only — do not switch back without a callable. */
  private readonly collectionName = 'admin_action_logs';

  async append(entry: Omit<AdminAuditLogEntry, 'id' | 'timestamp'> & { timestamp?: Date }): Promise<{ id: string }> {
    try {
      const { timestamp: _clientTs, ...rest } = entry;
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...rest,
        timestamp: serverTimestamp(),
      });
      return { id: docRef.id };
    } catch (error) {
      throw toDRXError(error, {
        code: 'ADMIN_AUDIT_APPEND_FAILED',
        devMessage: 'Failed to persist admin audit log',
      });
    }
  }

  async queryLogs(filters: {
    adminUserId?: string;
    targetUserId?: string;
    action?: string;
    limit?: number;
  } = {}): Promise<AdminAuditLogEntry[]> {
    try {
      let q = query(collection(db, this.collectionName));

      if (filters.adminUserId) {
        q = query(q, where('adminUserId', '==', filters.adminUserId));
      }
      if (filters.targetUserId) {
        q = query(q, where('targetUserId', '==', filters.targetUserId));
      }
      if (filters.action) {
        q = query(q, where('action', '==', filters.action));
      }

      q = query(q, orderBy('timestamp', 'desc'));

      if (filters.limit) {
        q = query(q, limit(filters.limit));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => {
        const data = d.data();
        const ts = data.timestamp;
        return {
          id: d.id,
          ...data,
          timestamp: ts && typeof ts.toDate === 'function' ? ts.toDate() : new Date(),
        } as AdminAuditLogEntry;
      });
    } catch (error) {
      throw toDRXError(error, {
        code: 'ADMIN_AUDIT_QUERY_FAILED',
        devMessage: 'Failed to query admin audit logs',
      });
    }
  }
}
